"use client";

import { useEffect } from "react";

interface FontFace {
    family: string;
    url: string | null | undefined;
}

/** Reference-counted set of font URLs currently registered in the document head. */
const activeFonts = new Map<string, { count: number; styleEl: HTMLStyleElement }>();

function fontFormatFromUrl(url: string): string {
    const lower = url.split("?")[0]!.toLowerCase();
    if (lower.endsWith(".woff2")) return "woff2";
    if (lower.endsWith(".woff")) return "woff";
    if (lower.endsWith(".ttf")) return "truetype";
    if (lower.endsWith(".otf")) return "opentype";
    return "truetype";
}

function key(family: string, url: string): string {
    return `${family}|${url}`;
}

/**
 * Inject `@font-face` rules for any URL-backed fonts in the given list and
 * remove them on unmount (reference counted, so multiple components can register
 * the same font without stomping each other).
 *
 * Built-in fonts (those without a URL) are no-ops.
 */
export function useBrandKitFontFaces(fonts: FontFace[]): void {
    useEffect(() => {
        const registered: string[] = [];

        for (const font of fonts) {
            if (!font.url || !font.family) continue;
            const k = key(font.family, font.url);
            registered.push(k);

            const existing = activeFonts.get(k);
            if (existing) {
                existing.count += 1;
                continue;
            }

            const styleEl = document.createElement("style");
            styleEl.dataset.brandFont = k;
            styleEl.textContent = `@font-face { font-family: ${JSON.stringify(
                font.family
            )}; src: url(${JSON.stringify(
                font.url
            )}) format(${JSON.stringify(fontFormatFromUrl(font.url))}); font-display: swap; }`;
            document.head.appendChild(styleEl);
            activeFonts.set(k, { count: 1, styleEl });
        }

        return () => {
            for (const k of registered) {
                const entry = activeFonts.get(k);
                if (!entry) continue;
                entry.count -= 1;
                if (entry.count <= 0) {
                    entry.styleEl.remove();
                    activeFonts.delete(k);
                }
            }
        };
        // We hash the array to a stable string so updates trigger re-registration.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fonts.map((f) => `${f.family}|${f.url ?? ""}`).join("\n")]);
}
